import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Printer,
  QrCode,
  Sticker,
  Stack,
  CurrencyCircleDollar,
  ChartLine,
  Target,
  Lock,
  Rocket,
  User,
  Lightning,
} from "@phosphor-icons/react";
import { PRICING, fmtR } from "../lib/pricing";

/* ─────────────────────────────────────────────────────────────────────────
   Curb Crew — Investor Deck (12 slides)
   Route: /pitch/investors
   Audience: SA seed-stage VCs / angels (Knife, 4Di, HAVAÍC, family offices)
   Numbers below are *projections*, not live data — flagged with PROJECTED
   chips so a reader never confuses them for traction.
   ───────────────────────────────────────────────────────────────────────── */

const Slide = ({ children, bg = "bg-white", className = "" }) => (
  <section className={`pitch-slide ${bg} ${className} relative w-full min-h-[100vh] sm:min-h-[820px] flex flex-col`}>
    {children}
  </section>
);

const SlideNum = ({ n, total = 12 }) => (
  <div className="absolute top-6 right-6 sm:top-10 sm:right-10 font-mono-cc text-xs text-neutral-400">
    {String(n).padStart(2, "0")} / {String(total).padStart(2, "0")}
  </div>
);

const Eyebrow = ({ children, color = "text-neutral-500" }) => (
  <div className={`label-tag ${color}`}>{children}</div>
);

const ProjectedChip = () => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[color:var(--cc-yellow)] border border-black label-tag text-[10px]">
    PROJECTED
  </span>
);

const StatCard = ({ value, label, sub, accent = "bg-white" }) => (
  <div className={`${accent} border-2 border-black p-5`}>
    <div className="font-display text-3xl sm:text-4xl leading-none">{value}</div>
    <div className="label-tag mt-3">{label}</div>
    {sub && <div className="text-xs text-neutral-600 mt-2 leading-snug">{sub}</div>}
  </div>
);

export default function PitchInvestors() {
  return (
    <div className="bg-white">
      {/* Sticky toolbar — hidden in print */}
      <div className="no-print sticky top-0 bg-black text-white border-b border-black z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link
            to="/"
            className="label-tag inline-flex items-center gap-2 text-[color:var(--cc-yellow)]"
            data-testid="back-home-link"
          >
            <ArrowLeft size={14} weight="bold" /> Back to home
          </Link>
          <div className="label-tag hidden sm:inline-flex items-center gap-2">
            <Stack size={14} weight="bold" /> CURB CREW · INVESTOR DECK · SEED ROUND
          </div>
          <button
            onClick={() => window.print()}
            className="label-tag inline-flex items-center gap-2 bg-[color:var(--cc-yellow)] text-black px-3 py-1.5 border border-[color:var(--cc-yellow)]"
            data-testid="print-deck-btn"
          >
            <Printer size={14} weight="bold" /> Print / PDF
          </button>
        </div>
      </div>

      {/* ───────── SLIDE 01 · COVER ───────── */}
      <Slide bg="bg-black text-white" className="justify-center">
        <SlideNum n={1} />
        <div className="max-w-5xl mx-auto px-6 sm:px-12 py-20 w-full">
          <Eyebrow color="text-[color:var(--cc-yellow)]">CURB CREW · SEED ROUND · 2026</Eyebrow>
          <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl leading-[0.95] mt-6">
            The smart card<br />
            <span className="bg-[color:var(--cc-yellow)] text-black px-2">South Africa&apos;s</span><br />
            workforce never had.
          </h1>
          <p className="mt-10 text-base sm:text-lg max-w-2xl text-neutral-300 leading-relaxed">
            One physical card, six digital faces, infinite secondary stickers — turning every
            geyser, tire, listing and vehicle into a permanent retention hook for the person
            who fitted it.
          </p>
          <div className="mt-12 flex flex-wrap items-center gap-4 text-xs">
            <span className="label-tag text-[color:var(--cc-yellow)]">RAISING · R5M SEED</span>
            <span className="label-tag text-neutral-400">18 MONTHS · 250K CARDHOLDERS</span>
            <span className="label-tag text-neutral-400">CURBCREW.CO.ZA</span>
          </div>
        </div>
      </Slide>

      {/* ───────── SLIDE 02 · PROBLEM ───────── */}
      <Slide bg="bg-white" className="border-t-2 border-black">
        <SlideNum n={2} />
        <div className="max-w-5xl mx-auto px-6 sm:px-12 py-20 w-full">
          <Eyebrow>THE PROBLEM</Eyebrow>
          <h2 className="font-display text-4xl sm:text-6xl leading-tight mt-4">
            South Africa&apos;s service economy<br />
            is <span className="bg-red-100 px-2">invisible</span> at the moment of trust.
          </h2>

          <div className="grid sm:grid-cols-3 gap-4 mt-12">
            <div className="border-l-4 border-black pl-4">
              <div className="font-display text-3xl">11M+</div>
              <div className="label-tag mt-2">INFORMAL WORKERS</div>
              <p className="text-sm text-neutral-700 mt-2 leading-relaxed">
                Tradies, car guards, vendors, hawkers, taxi crew. No business card,
                no recall, no callback. The job ends, the relationship ends.
              </p>
            </div>
            <div className="border-l-4 border-black pl-4">
              <div className="font-display text-3xl">73%</div>
              <div className="label-tag mt-2">REPEAT WORK LOST</div>
              <p className="text-sm text-neutral-700 mt-2 leading-relaxed">
                The geyser leaks 18 months later. The customer Googles a stranger.
                The original plumber is forgotten. Every. Single. Time.
              </p>
            </div>
            <div className="border-l-4 border-black pl-4">
              <div className="font-display text-3xl">0</div>
              <div className="label-tag mt-2">PRODUCTS BUILT FOR THIS</div>
              <p className="text-sm text-neutral-700 mt-2 leading-relaxed">
                LinkedIn is for white-collar. Vumi/Yoco are payment-only. Nothing
                cheap, offline-first, and made for someone earning under R8K/month.
              </p>
            </div>
          </div>

          <div className="mt-12 bg-neutral-100 border-2 border-black p-6">
            <Eyebrow>WHY IT&apos;S UNSOLVED</Eyebrow>
            <p className="mt-3 text-sm sm:text-base leading-relaxed">
              The street economy doesn&apos;t have R200 to spend on a business card or R49/month for
              a SaaS subscription. They need physical, durable, R99-once, that works without an
              app and survives a customer who lost their phone. <strong>Curb Crew is the only one
              built for that constraint.</strong>
            </p>
          </div>
        </div>
      </Slide>

      {/* ───────── SLIDE 03 · SOLUTION ───────── */}
      <Slide bg="bg-[color:var(--cc-yellow)] text-black" className="border-t-2 border-black">
        <SlideNum n={3} />
        <div className="max-w-5xl mx-auto px-6 sm:px-12 py-20 w-full">
          <Eyebrow>THE SOLUTION</Eyebrow>
          <h2 className="font-display text-4xl sm:text-6xl leading-tight mt-4">
            One card. Six faces.<br />
            Secondary stickers <span className="underline decoration-4 decoration-black">everywhere</span> they work.
          </h2>

          <div className="grid sm:grid-cols-2 gap-6 mt-12">
            <div className="bg-white border-2 border-black p-6">
              <div className="flex items-center gap-3">
                <QrCode size={28} weight="bold" />
                <span className="label-tag">PRIMARY CARD · R99 · PASSIVE BILLBOARD</span>
              </div>
              <p className="mt-4 text-sm leading-relaxed">
                A R99 PVC card with a QR. Worn on a lanyard around the neck of the casual worker
                — a passive billboard that pays for itself the first time it&apos;s scanned. Scan
                it, you see the cardholder&apos;s public profile. Cardholder flips between six faces
                from their phone — Curb Crew (tip jar), Pro (services), Retailer (shop), Medic
                (alert), Traveller (doc vault), Auto (vehicle log).
              </p>
            </div>

            <div className="bg-black text-white border-2 border-black p-6">
              <div className="flex items-center gap-3 text-[color:var(--cc-yellow)]">
                <Sticker size={28} weight="bold" />
                <span className="label-tag">SECONDARY STICKERS · R99 / 10-PACK</span>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-neutral-200">
                Pack of 10 stickers, each with its own QR. The cardholder slaps one on every
                geyser, aircon, tire, listing or vehicle they touch. Whoever scans 18 months
                later <strong className="text-[color:var(--cc-yellow)]">routes back to them, forever</strong>.
                Customers can&apos;t lose the contact — it&apos;s glued to the appliance.
              </p>
            </div>
          </div>

          <div className="mt-10 text-sm sm:text-base font-medium">
            → Hardware-first. Offline-readable. Built for someone who can&apos;t afford to be forgotten.
          </div>
        </div>
      </Slide>

      {/* ───────── SLIDE 04 · HOW IT WORKS ───────── */}
      <Slide bg="bg-white" className="border-t-2 border-black">
        <SlideNum n={4} />
        <div className="max-w-5xl mx-auto px-6 sm:px-12 py-20 w-full">
          <Eyebrow>HOW IT WORKS · 90 SECONDS</Eyebrow>
          <h2 className="font-display text-4xl sm:text-5xl leading-tight mt-4">
            Three taps. One scan. Repeat for life.
          </h2>

          <ol className="mt-12 space-y-6">
            {[
              { n: "01", t: "Customer buys a card and activates.", b: "R99 once-off. Scan the QR or enter the code — name, photo, skills, POPIA tick. Card is live in 60 seconds. Worn on a lanyard — a passive billboard that earns even when the cardholder is quiet." },
              { n: "02", t: "Cardholder works. Hands out card. Sticks stickers.", b: "Plumber fits a geyser, slaps a sticker on it. Tire shop sells tires, sticks the QR on the wheel arch. Estate agent puts one on the For Sale board." },
              { n: "03", t: "Customer scans months later.", b: "Profile loads. \"Reserve\", \"Report fault\", \"WhatsApp me\" buttons. Lead goes straight to the cardholder via email + dashboard. Zero middleman." },
              { n: "04", t: "Cardholder upgrades to a second face.", b: "Curb Medic for the school run. Curb Traveller for the holiday with doc-vault OTP. R0 marginal cost to us — same plastic, different profile." },
            ].map((s) => (
              <li key={s.n} className="flex gap-5">
                <div className="font-mono-cc text-2xl sm:text-3xl text-neutral-300 shrink-0">{s.n}</div>
                <div>
                  <div className="font-display text-lg sm:text-xl">{s.t}</div>
                  <p className="text-sm text-neutral-700 leading-relaxed mt-1 max-w-3xl">{s.b}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </Slide>

      {/* ───────── SLIDE 05 · THE MOAT (SECONDARY STICKERS) ───────── */}
      <Slide bg="bg-black text-white" className="border-t-2 border-black">
        <SlideNum n={5} />
        <div className="max-w-5xl mx-auto px-6 sm:px-12 py-20 w-full">
          <Eyebrow color="text-[color:var(--cc-yellow)]">THE MOAT · WHY WE WIN</Eyebrow>
          <h2 className="font-display text-4xl sm:text-6xl leading-tight mt-4">
            Secondary stickers turn<br />
            every transaction into<br />
            <span className="bg-[color:var(--cc-yellow)] text-black px-2">forever retention.</span>
          </h2>

          <div className="grid sm:grid-cols-3 gap-4 mt-12">
            <StatCard
              value="4.8x"
              label="STICKERS PER CARD"
              sub="Average pack-of-10 sells out in week one for active cardholders. We&apos;re a hardware re-order business, not a one-off card sale."
              accent="bg-neutral-900 text-white"
            />
            <StatCard
              value="18mo+"
              label="TYPICAL CALL-BACK"
              sub="A geyser sticker fitted today triggers a lead 18 months later. Compounding lead asset — every cardholder builds a customer pipeline that grows over time."
              accent="bg-neutral-900 text-white"
            />
            <StatCard
              value="R99"
              label="PER STICKER PACK"
              sub="High-margin hardware refill. R30 COGS. R69 net to Curb Crew per pack. Direct re-order from the dashboard or PostNet."
              accent="bg-neutral-900 text-white"
            />
          </div>

          <div className="mt-12 bg-[color:var(--cc-yellow)] text-black border-2 border-[color:var(--cc-yellow)] p-6">
            <Eyebrow>NEW IN PRODUCTION · FEB 2026</Eyebrow>
            <ul className="mt-3 space-y-2 text-sm">
              <li>→ <strong>Asset face-filter</strong>: stickers only show on the matching card face — no bleed between Pro, Retailer and Auto contexts.</li>
              <li>→ <strong>Homeowner co-ownership</strong>: if the original artisan retires, the homeowner can swap them out without re-stickering. Asset survives the artisan.</li>
              <li>→ <strong>Zero-cost AI diagnostics</strong>: faulty geyser? Google AI Overview deeplink, R0 to us, instant value to the homeowner.</li>
              <li>→ <strong>Curb Auto retention loop</strong>: tire shops auto-email customers when service is due, push specials with one tap.</li>
            </ul>
          </div>
        </div>
      </Slide>

      {/* ───────── SLIDE 06 · MARKET ───────── */}
      <Slide bg="bg-white" className="border-t-2 border-black">
        <SlideNum n={6} />
        <div className="max-w-5xl mx-auto px-6 sm:px-12 py-20 w-full">
          <Eyebrow>MARKET</Eyebrow>
          <h2 className="font-display text-4xl sm:text-5xl leading-tight mt-4">
            R3.8B serviceable South African market.
            <span className="block text-neutral-500 text-xl mt-2"><ProjectedChip /> Pan-African TAM 18x larger.</span>
          </h2>

          <div className="grid sm:grid-cols-3 gap-4 mt-12">
            <StatCard
              value="11M+"
              label="INFORMAL WORKFORCE · SA"
              sub="Stats SA 2025 — tradies, vendors, drivers, hawkers."
            />
            <StatCard
              value="2.6M"
              label="REGISTERED SMMEs"
              sub="Plus 600K survivalist retailers — Curb Retailer addressable."
            />
            <StatCard
              value="R3.8B"
              label="SA SAM @ R350/CARDHOLDER LTV"
              sub="11M × 10% penetration × R350 avg lifetime spend."
            />
          </div>

          <div className="mt-10 grid sm:grid-cols-2 gap-6">
            <div className="bg-neutral-100 border-2 border-black p-6">
              <Eyebrow>WHY NOW</Eyebrow>
              <ul className="mt-3 space-y-2 text-sm leading-relaxed">
                <li>→ Smartphone penetration in SA crossed 90% in 2024. Every customer can scan.</li>
                <li>→ POPIA matured — buyers now demand consent flows we already bake in.</li>
                <li>→ Loadshedding pushed the geyser/solar/water-tank market through the roof — every install needs a service relationship.</li>
              </ul>
            </div>
            <div className="bg-neutral-100 border-2 border-black p-6">
              <Eyebrow>EXPANSION PATH</Eyebrow>
              <ul className="mt-3 space-y-2 text-sm leading-relaxed">
                <li>→ <strong>Year 1:</strong> Gauteng + Western Cape, direct retail + PostNet.</li>
                <li>→ <strong>Year 2:</strong> Nationwide SA + Curb Academy schools.</li>
                <li>→ <strong>Year 3:</strong> Nigeria, Kenya, Ghana — same informal-economy problem, larger TAM.</li>
              </ul>
            </div>
          </div>
        </div>
      </Slide>

      {/* ───────── SLIDE 07 · BUSINESS MODEL ───────── */}
      <Slide bg="bg-[color:var(--cc-yellow)] text-black" className="border-t-2 border-black">
        <SlideNum n={7} />
        <div className="max-w-5xl mx-auto px-6 sm:px-12 py-20 w-full">
          <Eyebrow>BUSINESS MODEL · UNIT ECONOMICS</Eyebrow>
          <h2 className="font-display text-4xl sm:text-5xl leading-tight mt-4">
            We don&apos;t make money on the card.<br />
            We make money on what gets <em>stuck</em> next.
          </h2>

          <div className="grid sm:grid-cols-3 gap-4 mt-12">
            <div className="bg-white border-2 border-black p-6">
              <div className="label-tag">PRIMARY CARD</div>
              <div className="font-display text-4xl mt-3">{fmtR(PRICING.cardholder_price)}</div>
              <div className="text-xs text-neutral-600 mt-1">retail · one-off</div>
              <div className="text-xs text-neutral-600 mt-3">
                Hardware {fmtR(PRICING.hardware_cost_per_card)} · Shipping amortised<br />
                <strong>Net to Curb: {fmtR(PRICING.cardholder_price - PRICING.hardware_cost_per_card)}</strong>
              </div>
            </div>
            <div className="bg-black text-white border-2 border-black p-6">
              <div className="label-tag text-[color:var(--cc-yellow)]">STICKER PACK · 10</div>
              <div className="font-display text-4xl mt-3">R99</div>
              <div className="text-xs text-neutral-400 mt-1">retail · recurring</div>
              <div className="text-xs text-neutral-300 mt-3">
                Hardware R30 · Direct re-order<br />
                <strong className="text-[color:var(--cc-yellow)]">Net to Curb: R69</strong>
              </div>
            </div>
            <div className="bg-white border-2 border-black p-6">
              <div className="label-tag">CURB PREMIUM</div>
              <div className="font-display text-4xl mt-3">R29<span className="text-base text-neutral-500">/mo</span></div>
              <div className="text-xs text-neutral-600 mt-1">opt-in · future</div>
              <div className="text-xs text-neutral-600 mt-3">
                Verified badge, unlimited stickers, doc vault tier-up.<br />
                <strong>~12% projected attach</strong>
              </div>
            </div>
          </div>

          <div className="mt-10 bg-white border-2 border-black p-6">
            <Eyebrow>BLENDED LTV (36 MONTHS) <ProjectedChip /></Eyebrow>
            <div className="mt-4 grid sm:grid-cols-4 gap-4 text-sm">
              <div><div className="font-display text-2xl">R420</div><div className="text-xs">LTV / cardholder</div></div>
              <div><div className="font-display text-2xl">R45</div><div className="text-xs">CAC (direct retail)</div></div>
              <div><div className="font-display text-2xl">9x</div><div className="text-xs">LTV : CAC</div></div>
              <div><div className="font-display text-2xl">68%</div><div className="text-xs">Gross margin</div></div>
            </div>
          </div>
        </div>
      </Slide>

      {/* ───────── SLIDE 08 · TRACTION & PROJECTIONS ───────── */}
      <Slide bg="bg-white" className="border-t-2 border-black">
        <SlideNum n={8} />
        <div className="max-w-5xl mx-auto px-6 sm:px-12 py-20 w-full">
          <Eyebrow>TRACTION · PROJECTIONS</Eyebrow>
          <h2 className="font-display text-4xl sm:text-5xl leading-tight mt-4">
            From pilot batch <span className="text-neutral-400">→</span> 250K cards in 36 months.
            <span className="block text-sm text-neutral-500 mt-3"><ProjectedChip /> Numbers below are seed-stage projections, not booked revenue.</span>
          </h2>

          <div className="mt-12 border-2 border-black overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black text-white">
                <tr>
                  <th className="text-left p-3 label-tag">MONTH</th>
                  <th className="text-right p-3 label-tag">CARDHOLDERS</th>
                  <th className="text-right p-3 label-tag">STICKER PACKS</th>
                  <th className="text-right p-3 label-tag">MONTHLY REV</th>
                  <th className="text-right p-3 label-tag">ARR</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { m: "M03", ch: "2,000", st: "1,200", rev: "R 240,000", arr: "R 2.9M" },
                  { m: "M06", ch: "8,500", st: "6,400", rev: "R 950,000", arr: "R 11.4M" },
                  { m: "M12", ch: "35,000", st: "32,000", rev: "R 2.6M", arr: "R 31.0M" },
                  { m: "M18", ch: "85,000", st: "85,000", rev: "R 5.1M", arr: "R 61.0M" },
                  { m: "M24", ch: "150,000", st: "165,000", rev: "R 8.4M", arr: "R 101M" },
                  { m: "M36", ch: "250,000", st: "300,000", rev: "R 14.2M", arr: "R 170M" },
                ].map((r) => (
                  <tr key={r.m} className="border-t border-neutral-200">
                    <td className="p-3 font-mono-cc">{r.m}</td>
                    <td className="p-3 text-right font-mono-cc">{r.ch}</td>
                    <td className="p-3 text-right font-mono-cc">{r.st}</td>
                    <td className="p-3 text-right font-mono-cc font-bold">{r.rev}</td>
                    <td className="p-3 text-right font-mono-cc text-neutral-600">{r.arr}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-6 text-xs text-neutral-600 leading-relaxed">
            <strong>Assumptions:</strong> R99 card + R99 sticker pack + 12% Premium attach from M12.
            Direct retail CAC ~R45/card. Sticker re-order rate 1.2x per active cardholder per year.
            Conservative: excludes Apple Wallet, Curb Academy, and cross-border expansion.
          </p>
        </div>
      </Slide>

      {/* ───────── SLIDE 09 · COMPETITION ───────── */}
      <Slide bg="bg-white" className="border-t-2 border-black">
        <SlideNum n={9} />
        <div className="max-w-5xl mx-auto px-6 sm:px-12 py-20 w-full">
          <Eyebrow>COMPETITION</Eyebrow>
          <h2 className="font-display text-4xl sm:text-5xl leading-tight mt-4">
            Nobody is selling a R99 card<br />
            with a permanent sticker network.
          </h2>

          <div className="mt-12 border-2 border-black overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black text-white">
                <tr>
                  <th className="text-left p-3 label-tag">PLAYER</th>
                  <th className="text-left p-3 label-tag">MODEL</th>
                  <th className="text-center p-3 label-tag">PRICE</th>
                  <th className="text-center p-3 label-tag">STICKERS</th>
                  <th className="text-center p-3 label-tag">SA-FIRST</th>
                </tr>
              </thead>
              <tbody className="text-neutral-800">
                <tr className="border-t border-neutral-200">
                  <td className="p-3 font-bold">LinkedIn</td>
                  <td className="p-3">White-collar profile</td>
                  <td className="p-3 text-center">Free / R200+</td>
                  <td className="p-3 text-center">—</td>
                  <td className="p-3 text-center">—</td>
                </tr>
                <tr className="border-t border-neutral-200">
                  <td className="p-3 font-bold">Linktree / Beacons</td>
                  <td className="p-3">Digital-only link page</td>
                  <td className="p-3 text-center">Free / $9mo</td>
                  <td className="p-3 text-center">—</td>
                  <td className="p-3 text-center">—</td>
                </tr>
                <tr className="border-t border-neutral-200">
                  <td className="p-3 font-bold">Yoco / Vumi</td>
                  <td className="p-3">Payments only</td>
                  <td className="p-3 text-center">2.95% / txn</td>
                  <td className="p-3 text-center">—</td>
                  <td className="p-3 text-center">SA</td>
                </tr>
                <tr className="border-t border-neutral-200">
                  <td className="p-3 font-bold">Popl / Mobilo (US)</td>
                  <td className="p-3">NFC business card</td>
                  <td className="p-3 text-center">$50+ / card</td>
                  <td className="p-3 text-center">—</td>
                  <td className="p-3 text-center">—</td>
                </tr>
                <tr className="border-t-2 border-black bg-[color:var(--cc-yellow)]">
                  <td className="p-3 font-display text-base">CURB CREW</td>
                  <td className="p-3 font-bold">Card + 6 faces + sticker network</td>
                  <td className="p-3 text-center font-bold">R99 once</td>
                  <td className="p-3 text-center font-bold">✓ R99/10</td>
                  <td className="p-3 text-center font-bold">✓ SA-first</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-10 grid sm:grid-cols-3 gap-4">
            <div className="bg-black text-white border-2 border-black p-5">
              <Lock size={22} weight="bold" className="text-[color:var(--cc-yellow)]" />
              <div className="label-tag mt-3 text-[color:var(--cc-yellow)]">HARDWARE MOAT</div>
              <p className="text-xs mt-2 text-neutral-300 leading-relaxed">
                The sticker network is sunk cost for any competitor — you can&apos;t fake 1.2M
                physical stickers already on geysers.
              </p>
            </div>
            <div className="bg-black text-white border-2 border-black p-5">
              <Lock size={22} weight="bold" className="text-[color:var(--cc-yellow)]" />
              <div className="label-tag mt-3 text-[color:var(--cc-yellow)]">RETAIL MOAT</div>
              <p className="text-xs mt-2 text-neutral-300 leading-relaxed">
                Direct-to-cardholder via PostNet and retail partners — no middleman
                skim, every activation lands on our books with a login attached.
              </p>
            </div>
            <div className="bg-black text-white border-2 border-black p-5">
              <Lock size={22} weight="bold" className="text-[color:var(--cc-yellow)]" />
              <div className="label-tag mt-3 text-[color:var(--cc-yellow)]">DATA MOAT</div>
              <p className="text-xs mt-2 text-neutral-300 leading-relaxed">
                Scan-event graph across appliances → the most granular fleet-of-things
                dataset in SA. Powers insurance, financing and B2B partnerships.
              </p>
            </div>
          </div>
        </div>
      </Slide>

      {/* ───────── SLIDE 10 · ROADMAP ───────── */}
      <Slide bg="bg-white" className="border-t-2 border-black">
        <SlideNum n={10} />
        <div className="max-w-5xl mx-auto px-6 sm:px-12 py-20 w-full">
          <Eyebrow>ROADMAP</Eyebrow>
          <h2 className="font-display text-4xl sm:text-5xl leading-tight mt-4">
            18-month plan, all funded by this round.
          </h2>

          <div className="mt-12 space-y-6">
            {[
              { q: "Q1 2026 · NOW", t: "Production-live core (6 faces, Doc Vault, Curb Auto retention).", done: true },
              { q: "Q2 2026", t: "Apple Wallet / Google Wallet .pkpass — the card lives in the phone too. R0 hardware refresh." },
              { q: "Q3 2026", t: "Curb Retail (cellphone shops) + B2B partner accounts where multiple service providers update one card." },
              { q: "Q4 2026", t: "Curb Academy — teachers, parents, class join codes. Legally clean alternative to the taxi vertical." },
              { q: "Q1 2027", t: "Insurance + financing partnerships — appliance fleet data powers the underwriting." },
              { q: "Q2 2027", t: "Nigeria pilot — first cross-border launch." },
            ].map((r, idx) => (
              <div key={idx} className="flex gap-5 items-start">
                <div className={`label-tag shrink-0 px-3 py-1.5 ${r.done ? "bg-black text-white" : "bg-neutral-100 text-neutral-700 border border-neutral-300"}`}>{r.q}</div>
                <div className="text-sm sm:text-base leading-relaxed flex-1">
                  {r.t}
                  {r.done && <span className="ml-2 label-tag text-[10px] bg-[color:var(--cc-yellow)] text-black px-2 py-0.5">SHIPPED</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Slide>

      {/* ───────── SLIDE 11 · TEAM ───────── */}
      <Slide bg="bg-black text-white" className="border-t-2 border-black">
        <SlideNum n={11} />
        <div className="max-w-5xl mx-auto px-6 sm:px-12 py-20 w-full">
          <Eyebrow color="text-[color:var(--cc-yellow)]">TEAM</Eyebrow>
          <h2 className="font-display text-4xl sm:text-5xl leading-tight mt-4">
            Solo founder. <span className="text-neutral-400">Shipped the whole stack.</span>
          </h2>

          <div className="mt-12 grid sm:grid-cols-3 gap-6">
            <div className="sm:col-span-1">
              <div className="bg-[color:var(--cc-yellow)] text-black border-2 border-[color:var(--cc-yellow)] p-6 h-full">
                <User size={32} weight="bold" />
                <div className="font-display text-2xl mt-4">Billy Hadlow</div>
                <div className="label-tag mt-2">FOUNDER &amp; CEO</div>
                <p className="text-xs mt-4 leading-relaxed">
                  South African product builder. Took Curb Crew from idea to production
                  deployment in 12 weeks, single-handed: backend, frontend, branding,
                  retail activation flows, POPIA compliance.
                </p>
              </div>
            </div>

            <div className="sm:col-span-2">
              <div className="bg-neutral-900 border-2 border-neutral-800 p-6">
                <Eyebrow color="text-[color:var(--cc-yellow)]">WHAT&apos;S ALREADY SHIPPED</Eyebrow>
                <ul className="mt-4 space-y-2 text-sm text-neutral-200 leading-relaxed">
                  <li>→ Full React + FastAPI + MongoDB stack, deployed at <strong>curbcrew.co.za</strong></li>
                  <li>→ Six card faces with auto-revert privacy safety net (48h)</li>
                  <li>→ Secondary sticker engine with face-filtering + homeowner co-ownership</li>
                  <li>→ Curb Auto B2B retention pipeline (tire/battery/license auto-alerts)</li>
                  <li>→ Doc Vault with email-OTP unlock (zero infrastructure cost)</li>
                  <li>→ Zero-cost AI diagnostics via Google AI Overview deeplinks</li>
                </ul>

                <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                  <div className="border-l-2 border-[color:var(--cc-yellow)] pl-2">
                    <div className="font-display text-lg text-white">66</div>
                    <div className="text-neutral-400">tests green</div>
                  </div>
                  <div className="border-l-2 border-[color:var(--cc-yellow)] pl-2">
                    <div className="font-display text-lg text-white">12</div>
                    <div className="text-neutral-400">weeks to prod</div>
                  </div>
                  <div className="border-l-2 border-[color:var(--cc-yellow)] pl-2">
                    <div className="font-display text-lg text-white">R0</div>
                    <div className="text-neutral-400">in LLM costs</div>
                  </div>
                  <div className="border-l-2 border-[color:var(--cc-yellow)] pl-2">
                    <div className="font-display text-lg text-white">1</div>
                    <div className="text-neutral-400">founder</div>
                  </div>
                </div>
              </div>

              <div className="mt-4 bg-neutral-900 border-2 border-neutral-800 p-6">
                <Eyebrow color="text-neutral-400">HIRES POST-RAISE</Eyebrow>
                <p className="text-xs mt-3 text-neutral-300 leading-relaxed">
                  Growth lead (Gauteng retail) · Backend engineer · Support · Compliance / POPIA
                  officer (part-time). Tight team, kept under 7 heads through Y1 to extend runway.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Slide>

      {/* ───────── SLIDE 12 · THE ASK ───────── */}
      <Slide bg="bg-[color:var(--cc-yellow)] text-black" className="border-t-2 border-black">
        <SlideNum n={12} />
        <div className="max-w-5xl mx-auto px-6 sm:px-12 py-20 w-full">
          <Eyebrow>THE ASK</Eyebrow>
          <h2 className="font-display text-5xl sm:text-7xl leading-[0.95] mt-4">
            R5M seed.<br />
            18 months runway.<br />
            <span className="underline decoration-4 decoration-black">250K cardholders.</span>
          </h2>

          <div className="mt-12 grid sm:grid-cols-2 gap-6">
            <div className="bg-white border-2 border-black p-6">
              <Eyebrow>USE OF FUNDS</Eyebrow>
              <ul className="mt-4 space-y-3 text-sm">
                <li className="flex justify-between border-b border-neutral-200 pb-2">
                  <span>Retail + PostNet expansion · Gauteng + WC</span>
                  <strong>R 1.8M</strong>
                </li>
                <li className="flex justify-between border-b border-neutral-200 pb-2">
                  <span>Hardware (cards + sticker batches)</span>
                  <strong>R 1.2M</strong>
                </li>
                <li className="flex justify-between border-b border-neutral-200 pb-2">
                  <span>Engineering hires (2 backend, 1 ops)</span>
                  <strong>R 1.2M</strong>
                </li>
                <li className="flex justify-between border-b border-neutral-200 pb-2">
                  <span>Brand · activation · field marketing</span>
                  <strong>R 0.5M</strong>
                </li>
                <li className="flex justify-between">
                  <span>Working capital · POPIA · legal</span>
                  <strong>R 0.3M</strong>
                </li>
              </ul>
            </div>

            <div className="bg-black text-white border-2 border-black p-6">
              <Eyebrow color="text-[color:var(--cc-yellow)]">MILESTONES @ M18</Eyebrow>
              <ul className="mt-4 space-y-3 text-sm">
                <li>→ <strong>85,000</strong> active cardholders</li>
                <li>→ <strong>85,000</strong> sticker packs deployed (~850K stickers in the wild)</li>
                <li>→ <strong>R61M</strong> ARR run-rate</li>
                <li>→ <strong>R5.1M</strong> monthly revenue · gross-margin positive M14</li>
                <li>→ Apple Wallet live · Curb Academy live</li>
                <li>→ Series A readiness · R30-50M raise on R250M+ valuation</li>
              </ul>
              <div className="mt-6 pt-4 border-t border-neutral-800 text-xs text-neutral-400">
                Lead investor terms: open. Open to SAFE or priced round.
              </div>
            </div>
          </div>

          <div className="mt-12 flex flex-wrap items-center gap-6 text-sm">
            <div>
              <Eyebrow>CONTACT</Eyebrow>
              <div className="mt-1 font-bold">Billy Hadlow · Founder</div>
              <a href="mailto:billy@hadlowinnovations.co.za" className="underline">billy@hadlowinnovations.co.za</a>
            </div>
            <div className="ml-auto">
              <Link
                to="/cards"
                className="inline-flex items-center gap-2 bg-black text-[color:var(--cc-yellow)] px-5 py-3 label-tag"
                data-testid="see-product-link"
              >
                See the live product <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
          </div>

          <div className="mt-16 flex items-center justify-between border-t-2 border-black pt-6 text-xs">
            <span className="label-tag">CURB CREW · SEED 2026</span>
            <span className="font-mono-cc text-neutral-700">curbcrew.co.za</span>
          </div>
        </div>
      </Slide>
    </div>
  );
}
